import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import moment from 'moment';
import { IDatepickerConfig, IMonth, IPeriodValue, IDatepickerValue } from '../types';
import { IDatePeriod } from '../types/picker.type';
import { get, isNil } from 'lodash';

@Component({
  selector: 'xux-datepicker-periods',
  templateUrl: './xux-datepicker-periods.component.html',
  styleUrls: ['./xux-datepicker-periods.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class XuxDatepickerPeriodsComponent implements OnInit {
  @Input() public config: IDatepickerConfig;

  @Input('calendarValue') set _calendarValue(_value: IDatepickerValue) {
    this.calendarValue = _value ? _value.value : null;
    if (!this._periods || !this._periods.length) {
      return;
    }

    this.setActivePeriod(this.calendarValue);
  }

  public value: IPeriodValue = {
    year: moment().year(),
    month: {
      number: null,
      name: null,
    },
    period: {
      dateStart: null,
      dateEnd: null,
    },
  };
  public months: IMonth[] = [];

  public get years(): IDatePeriod[] {
    return this._periods
      .reduce((result, period) => {
        const yearStart = period.dateStart instanceof Date
                          ? moment(period.dateStart.getFullYear(), 'YYYY').format('YYYY')
                          : moment(period.dateStart).format('YYYY');
        if (result.indexOf(yearStart) === -1) {
          if(!isNil(this.config.minDate)) {
            const minDate = moment(this.config.minDate).format('YYYY')
            if(moment(minDate).isSameOrBefore(moment(period.dateStart).format('YYYY'))){
              result.push(yearStart);
            }
          } else {
            result.push(yearStart);
          }
        }
        return result;
      }, [])
      .sort((a, b) => b - a);
  }

  public get periods(): IDatePeriod[] {
    return this._periods
      .filter(period => {
        const dateStart = moment(period.dateStart);
        const dateEnd = moment(period.dateEnd);
        const month = moment([this.value.year, this.value.month.number - 1]);
        if(!isNil(this.config.minDate)) {
          const minDate = moment(this.config.minDate);

          return minDate.isSameOrBefore(dateStart) && (month.format('YYYY-MM') === dateStart.format('YYYY-MM') || month.isBetween(dateStart, dateEnd));
        }
        return month.format('YYYY-MM') === dateStart.format('YYYY-MM') || month.isBetween(dateStart, dateEnd);
      });
  }
  @Input('periods') public _periods: IDatePeriod[];
  @Output() public onChange: EventEmitter<IDatePeriod> = new EventEmitter();
  private calendarValue: IDatePeriod;

  constructor() {
    this.months = moment.monthsShort()
      .map((name, index) => {
        const monthName = (name.length > 4 && name.indexOf('.') === -1) ? `${name.slice(0, 3)}.` : name;
        return { number: (index + 1), name: monthName };
      });
  }

  public ngOnInit(): void {
    if (this.calendarValue) {
      this.setActivePeriod(this.calendarValue);
    } else {
      const dateNow = moment();
      const monthNow = this.months.find(month => month.number === dateNow.month() + 1);

      this.selectYear(dateNow.year());
      this.selectMonth(monthNow);

      const filteredPeriods = this.periods.filter(period => {
        const format = this.config.dateFormat.short_date_format;
        const dateStart = moment(period.dateStart, format);
        const dateEnd = moment(period.dateEnd, format);
        return moment().isBetween(dateStart, dateEnd);
      });
      if (filteredPeriods.length) {
        const defaultPeriod = filteredPeriods
          .reduce((result, period) => {
            return result.dateStart > period.dateStart ? period : result;
          }, filteredPeriods[0]);
        this.selectPeriod(defaultPeriod);
      }
    }
  }

  public selectYear(year: number): void {
    this.value.year = year;
    this.selectMonth(this.months[0]);
  }

  public selectMonth(month: IMonth): void {
    this.value.month = month;
  }

  public selectPeriod(period: IDatePeriod): void {
    const format = this.config.dateFormat.short_date_format;
    this.value.period = period;
    const dateStart = moment(period.dateStart, format).toDate();
    const dateEnd = moment(period.dateEnd, format).toDate();
    this.onChange.emit({ dateStart, dateEnd });
  }

  public formatDate(date: Date, format: string, subtract: number = 0): string {
    if (subtract) {
      return date ? moment(date).subtract(subtract, 'd').format(format) : '';
    } else {
      return date ? moment(date).format(format) : '';
    }
  }

  private setActivePeriod(period?: IDatePeriod): void {
    if (!period) {
      return;
    }

    const periodValue = this._periods.find((periodItem) => {
      return moment(periodItem.dateStart).isSame(period.dateStart, 'day') &&
          moment(periodItem.dateEnd).isSame(period.dateEnd, 'day');
    });
    const year = moment(period.dateStart).year();
    this.selectYear(year);

    if(!isNil(get(this.config, 'minDate', null))) {
      const minDateMonth = moment(this.config.minDate).month();

      this.months = this.months.filter((month) => month.number > minDateMonth)
    }

    const month = this.months.find(item => item.number === moment(period.dateStart).month() + 1);
    this.selectMonth(month);
    this.value.period = periodValue;
  }
}
